import React, { useContext, useEffect, useRef, useState } from "react";
import {
  PIANO_KEYS,
  midiToKey,
  playNote,
  playChord,
  playClick,
  COUNT_IN_BEATS,
} from "../lib/keyboardPlan";

// Playback prefs (tempo in ms-per-beat, count-in on/off) shared with every
// PlayButton without prop-threading. NumberSense provides the live settings;
// the defaults keep standalone usage (and tests) working unconfigured.
export const AudioSettingsContext = React.createContext({
  tempo: 720,
  countIn: false,
});

/* ============================================================
   Number Sense — shared little UI atoms
   Inline icons + the on-screen clickable piano, shared by the
   lesson screens and the practice games.
   ============================================================ */

export const ICONS = {
  flame: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c1 3-1 4-1 6 0 1 1 2 2 2 1-1 1-2 1-3 2 2 3 4 3 7a6 6 0 1 1-12 0c0-3 2-5 3-7 1 2 2 2 3 1 1-2 0-4-2-6z"/></svg>',
  bolt: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z"/></svg>',
  heart: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 21s-7-4.5-9.5-9C.8 8.7 2.5 5 6 5c2 0 3 1.2 4 2.5C11 6.2 12 5 14 5c3.5 0 5.2 3.7 3.5 7-2.5 4.5-9.5 9-9.5 9z"/></svg>',
  check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 13l4 4L19 7"/></svg>',
  note: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="6" cy="18" r="3"/><circle cx="17" cy="15" r="3"/><path d="M9 18V5l11-2v12"/></svg>',
  lock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
  trophy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 4h10v4a5 5 0 0 1-10 0zM7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3M9 17h6M10 17l1-4h2l1 4M8 21h8"/></svg>',
  heartbreak: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 21s-7-4.5-9.5-9C.8 8.7 2.5 5 6 5c2 0 3 1.2 4 2.5C11 6.2 12 5 14 5c3.5 0 5.2 3.7 3.5 7-2.5 4.5-9.5 9-9.5 9z"/><path d="M12 7l-2 4 3 2-2 4" stroke="#15161a"/></svg>',
  play: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 4v16l13-8z"/></svg>',
  timer: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="13" r="8"/><path d="M12 13V9M9 2h6"/></svg>',
  swap: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 4 3 8l4 4M3 8h13M17 20l4-4-4-4M21 16H8"/></svg>',
  piano: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M8 4v9M12 4v9M16 4v9M3 13h18"/></svg>',
  ear: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9a6 6 0 1 1 12 0c0 4-4 4-4 7a3 3 0 0 1-6 0M9 9a3 3 0 0 1 6 0"/></svg>',
  music: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="6" cy="18" r="3"/><circle cx="17" cy="15" r="3"/><path d="M9 18V5l11-2v12"/></svg>',
  back: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 5l-7 7 7 7"/></svg>',
  cards: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="6" width="11" height="14" rx="2"/><path d="M9 4h9a2 2 0 0 1 2 2v11"/></svg>',
  chart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4v16h16"/><rect x="7" y="11" width="3" height="6"/><rect x="12" y="7" width="3" height="10"/><rect x="17" y="13" width="3" height="4"/></svg>',
  link: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 15l6-6"/><path d="M11 6l1.5-1.5a4 4 0 0 1 5.5 5.5L16.5 11"/><path d="M13 18l-1.5 1.5a4 4 0 0 1-5.5-5.5L7.5 13"/></svg>',
  today: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/></svg>',
  gear: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1"/></svg>',
  book: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z"/><path d="M19 17H6a2 2 0 0 0-2 2"/></svg>',
  grid: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="4" width="7" height="7" rx="1.5"/><rect x="13" y="4" width="7" height="7" rx="1.5"/><rect x="4" y="13" width="7" height="7" rx="1.5"/><rect x="13" y="13" width="7" height="7" rx="1.5"/></svg>',
};

export const Icon = ({ name }) => (
  <span dangerouslySetInnerHTML={{ __html: ICONS[name] }} />
);

// The signature little keyboard mark — five mini piano keys.
export const Keys = () => (
  <span className="ns-keysmark">
    <i className="w" />
    <i className="b" />
    <i className="w" />
    <i className="b" />
    <i className="w" />
  </span>
);

export const Html = ({ html, className }) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: html }} />
);

/* ============================================================
   On-screen piano — two octaves (C4–C6), click to sound a note.

   Controlled by the parent: `pressed` is a Set of pitch-class names the
   parent is collecting; tapping a key calls onPress(key). When `result` is
   "right" / "wrong", keys in `targetNotes` flash to give chord feedback.
   Validation is octave-agnostic (a chord is a chord in any register), so the
   parent compares pitch-class sets, not specific keys.
   ============================================================ */
const WHITE_KEYS = PIANO_KEYS.filter((k) => !k.black);
const W = 100 / WHITE_KEYS.length; // % width per white key
const BW = W * 0.62; // black-key width

// A screen-reader-friendly name for a key, e.g. "C sharp 4" rather than "C#4"
// (which assistive tech reads as "C number sign 4").
const spokenKey = (k) => `${k.name.replace("#", " sharp")} ${k.octave}`;

export function Piano({
  pressed,
  onPress,
  targetNotes = [],
  hint = [],
  result = null,
  disabled = false,
  exactKeys = null,
  // exactHint (a Set of "C4"-style ids) hints one specific key — pitch-class
  // hints light every octave, which misleads octave-exact exercises like the
  // guided scale walk.
  exactHint = null,
}) {
  const target = new Set(targetNotes);
  const hinted = new Set(hint);
  return (
    <div className="ns-piano-wrap">
      {/* group role so assistive tech announces this as one piano control */}
      <div className="ns-piano" role="group" aria-label="on-screen piano">
        {PIANO_KEYS.map((k) => {
          const id = `${k.name}${k.octave}`;
          // exactKeys (a Set of "C4"-style ids) highlights specific octaves —
          // needed for inversions where which octave a note is in matters.
          const isPressed = exactKeys ? exactKeys.has(id) : pressed.has(k.name);
          const left = k.black ? (k.wi + 1) * W - BW / 2 : k.wi * W;
          const width = k.black ? BW : W;
          let state = "";
          if (result && target.has(k.name)) state = ` ${result}`;
          else if (isPressed) state = " sel";
          else if (hinted.has(k.name) || (exactHint && exactHint.has(id))) state = " hint";
          const typing = TYPING_KEY_BY_MIDI[k.midi];
          return (
            <button
              key={id}
              type="button"
              aria-label={spokenKey(k)}
              aria-pressed={isPressed}
              className={`pk ${k.black ? "black" : "white"}${state}`}
              style={{ left: `${left}%`, width: `${width}%` }}
              disabled={disabled}
              onClick={() => {
                playNote(k.midi);
                if (!disabled) onPress(k);
              }}
            >
              {/* the typing-keyboard key-cap, like the synth lab's keyboard
                  (CSS hides it on touch devices, where there's no keyboard) */}
              {typing && (
                <span className="pk-kb" aria-hidden="true">
                  {typing.toUpperCase()}
                </span>
              )}
              {!k.black && <span className="pk-label">{k.name}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Typing-keyboard piano input — most players won't have a MIDI
   keyboard, but everyone has the one they type on. The home row
   plays the white keys from middle C (A S D F G H J K L ;) and the
   row above plays the black keys between them (W E · T Y U · O P),
   the standard virtual-piano layout. The hook emits the same
   { name, octave, midi } key shape the on-screen Piano and Web MIDI
   produce, so it feeds the same collectors.
   ============================================================ */
export const KEYBOARD_TO_MIDI = {
  a: 60, // C4
  w: 61,
  s: 62,
  e: 63,
  d: 64,
  f: 65,
  t: 66,
  g: 67,
  y: 68,
  h: 69,
  u: 70,
  j: 71,
  k: 72, // C5
  o: 73,
  l: 74,
  p: 75,
  ";": 76, // E5
};

// midi → typing character, for the key-cap labels drawn on the piano.
const TYPING_KEY_BY_MIDI = Object.fromEntries(
  Object.entries(KEYBOARD_TO_MIDI).map(([ch, midi]) => [midi, ch])
);

export function useTypingPiano(onPress, enabled = true) {
  const cb = useRef(onPress);
  cb.current = onPress;
  useEffect(() => {
    if (!enabled) return undefined;
    const down = (e) => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target;
      if (
        el &&
        (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)
      )
        return;
      const midi = KEYBOARD_TO_MIDI[String(e.key).toLowerCase()];
      if (midi == null) return;
      e.preventDefault();
      cb.current(midiToKey(midi));
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, [enabled]);
}

// The one-line nudge that teaches the mapping (the piano also draws each
// mapped letter as a key-cap on its key, like the synth lab's keyboard).
// Hidden once a real MIDI keyboard is connected, and on touch devices via CSS
// — no typing keyboard there.
export function TypingHint({ midiStatus }) {
  if (midiStatus === "connected") return null;
  return (
    <div className="ns-typing-hint">
      no MIDI keyboard? yours types too — the letters on the keys play them
      (<span className="mono">A</span> is middle C)
    </div>
  );
}

// A small affordance shown near the piano when Web MIDI is usable: confirms a
// real keyboard is connected (so the player knows they can skip the screen) or
// nudges them to plug one in. Renders nothing where MIDI isn't an option.
export function MidiBadge({ status, devices = 0 }) {
  if (status === "unsupported" || status === "idle") return null;
  const connected = status === "connected";
  const text =
    status === "connected"
      ? `MIDI keyboard connected${devices > 1 ? ` (${devices})` : ""}`
      : status === "denied"
      ? "MIDI access blocked"
      : "connect a MIDI keyboard to play along";
  return (
    <div className={`ns-midi ${connected ? "on" : ""}`}>
      <span className="ns-midi-dot" aria-hidden="true" />
      <Icon name="piano" />
      <span>{text}</span>
    </div>
  );
}

// A small "hear it" button that plays an array of chord note-arrays in time.
// Tempo and count-in default to the shared audio settings; pass `beatMs` or
// `countIn` to override (e.g. a short answer-reveal that wants no count-in).
export function PlayButton({ chords, beatMs, countIn, label = "hear it", big = false }) {
  const settings = useContext(AudioSettingsContext);
  const tempo = beatMs ?? settings.tempo;
  const lead = (countIn ?? settings.countIn) ? COUNT_IN_BEATS : 0;
  const [playing, setPlaying] = useState(false);
  const run = () => {
    if (!chords || !chords.length) return;
    for (let i = 0; i < lead; i += 1) playClick((i * tempo) / 1000, i === 0);
    const offset = (lead * tempo) / 1000;
    chords.forEach((notes, i) =>
      playChord(notes, tempo * 0.95, offset + (i * tempo) / 1000)
    );
    setPlaying(true);
    window.setTimeout(() => setPlaying(false), (lead + chords.length) * tempo);
  };
  return (
    <button
      type="button"
      className={`ns-play ${big ? "big" : ""} ${playing ? "on" : ""}`}
      onClick={run}
    >
      <Icon name="play" />
      <span>{label}</span>
    </button>
  );
}

// Slot-by-slot ear answering (issue #86): a chart of slots — one per bar
// heard — filled from the key's whole degree row. Tapping a degree fills the
// next open slot; the ⌫ chip clears the last one. Once graded (`results` is
// the per-slot correctness array) each slot reads hit or miss. Shared by the
// lesson runtime and the arcade's ear game.
export function EarSlots({ pool, count, picks, onPicks, answered, results }) {
  const fill = (deg) => {
    if (answered || picks.length >= count) return;
    onPicks([...picks, deg]);
  };
  const undo = () => {
    if (answered || !picks.length) return;
    onPicks(picks.slice(0, -1));
  };
  return (
    <>
      <div className="ns-prog ns-earslots">
        {Array.from({ length: count }, (_, i) => {
          let cls = "ns-prog-step";
          if (answered && results) cls += results[i] ? " hit" : " miss";
          else if (picks[i] == null) cls += " blank";
          if (!answered && i === picks.length) cls += " current";
          return (
            <div className={cls} key={i}>
              <span className="mono num">{picks[i] != null ? picks[i] : "?"}</span>
            </div>
          );
        })}
        <button
          type="button"
          className="ns-ear-undo"
          onClick={undo}
          disabled={answered || !picks.length}
          aria-label="remove last answer"
        >
          ⌫
        </button>
      </div>
      <div className="choices many ns-earpad">
        {pool.map((deg) => (
          <button
            key={deg}
            type="button"
            className="choice"
            disabled={answered || picks.length >= count}
            onClick={() => fill(deg)}
          >
            <span className="mono">{deg}</span>
          </button>
        ))}
      </div>
    </>
  );
}
